import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  LabelList,
  Cell,
} from "recharts";
import type { TooltipContentProps } from "recharts/types/component/Tooltip";
import { DashboardData } from "../../types";
import {
  aggregateReasonsByGrouping,
  COLORS,
  getNonComplianceReasonData,
  getReasonInputStats,
  orderedTimings,
  ReasonAggregationGrouping,
} from "./utils/dataProcessing";
import {
  createTooltipItemSorter,
  ReasonLegend,
} from "./utils/chartUtils";

interface ReasonTabProps {
  data: DashboardData;
  hasReasonData: boolean;
  isChartCaptureMode?: boolean;
}

export type ReasonTabHandle = {
  setGrouping: (grouping: ReasonAggregationGrouping) => void;
  getCurrentGrouping: () => ReasonAggregationGrouping;
};

const REASON_COLORS = COLORS.slice(0, 15);
type ReasonRow = Record<string, number> & { total?: number };
const LABEL_WRAP_THRESHOLD = 14;
const TOTAL_CELL_SX = { backgroundColor: "#f5f5f5" };

const groupingLabelMap: Record<ReasonAggregationGrouping, string> = {
  department: "部署",
  occupation: "職種",
  timing: "観察タイミング",
  departmentOccupation: "グループ",
  departmentTiming: "グループ",
  departmentOccupationTiming: "グループ",
};

const renderReasonSummaryTooltip = ({
  active,
  payload,
}: TooltipContentProps) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const reason = payload[0].payload?.name ?? payload[0].name;
  const value = payload[0].value;

  return (
    <Paper sx={{ p: 1 }}>
      <Typography variant="body2">{`${reason}：${String(value ?? "")}件`}</Typography>
    </Paper>
  );
};

const ReasonTab = forwardRef<ReasonTabHandle, ReasonTabProps>(
  ({ data, hasReasonData, isChartCaptureMode = false }, ref) => {
    const [grouping, setGrouping] =
      useState<ReasonAggregationGrouping>("department");

    const reasonStats = useMemo(() => getReasonInputStats(data), [data]);
    const allReasons = useMemo(() => getNonComplianceReasonData(data), [data]);

    const aggregation = useMemo(
      () =>
        aggregateReasonsByGrouping(
          data,
          grouping,
          allReasons.map((reason) => reason.name),
        ),
      [data, grouping, allReasons],
    );

    const tooltipItemSorter = useMemo(
      () => createTooltipItemSorter(aggregation.reasons),
      [aggregation.reasons],
    );

    const reasonColors = useMemo(() => {
      const map: Record<string, string> = {};
      aggregation.reasons.forEach((reason, index) => {
        map[reason] = REASON_COLORS[index % REASON_COLORS.length];
      });
      return map;
    }, [aggregation.reasons]);

    const reasonLabelWidth = useMemo(() => {
      if (allReasons.length === 0) {
        return 160;
      }

      const longestLabelLength = allReasons.reduce(
        (max, reason) => Math.max(max, reason.name.length),
        0,
      );

      return Math.min(Math.max(180, longestLabelLength * 14), 320);
    }, [allReasons]);

    const reasonChartHeight = useMemo(
      () => Math.max(360, allReasons.length * 44),
      [allReasons.length],
    );

    const sortDepartments = (a: string, b: string) => {
      const nameA = a || "";
      const nameB = b || "";

      const regexResult1 = nameA.match(/^([A-Za-z]+)(\d+)$/);
      const regexResult2 = nameB.match(/^([A-Za-z]+)(\d+)$/);

      if (!regexResult1 || !regexResult2) {
        return nameA.localeCompare(nameB);
      }

      const [, letterA, numberA] = regexResult1;
      const [, letterB, numberB] = regexResult2;

      const letterCompare = letterA.localeCompare(letterB);
      if (letterCompare !== 0) {
        return letterCompare;
      }

      return parseInt(numberA, 10) - parseInt(numberB, 10);
    };

    const createEmptyReasonRow = (): ReasonRow => {
      const row: ReasonRow = {};
      aggregation.reasons.forEach((reason) => {
        row[reason] = 0;
      });
      return row;
    };

    const sumReasonRows = (rows: ReasonRow[]) => {
      const totals = createEmptyReasonRow();
      let totalCount = 0;

      rows.forEach((row) => {
        aggregation.reasons.forEach((reason) => {
          totals[reason] += Number(row[reason] || 0);
        });
        totalCount += Number(row.total || 0);
      });

      return { totals, totalCount };
    };

    const renderReasonCells = (
      row: ReasonRow,
      options?: { isBold?: boolean },
    ) =>
      aggregation.reasons.map((reason) => (
        <TableCell
          key={reason}
          align="center"
          sx={options?.isBold ? { fontWeight: "bold" } : undefined}
        >
          {Number(row[reason] || 0)}
        </TableCell>
      ));

    const getLabelSx = (label: string, extra?: Record<string, unknown>) => {
      const shouldWrap = label.length > LABEL_WRAP_THRESHOLD;
      return {
        whiteSpace: shouldWrap ? "normal" : "nowrap",
        wordBreak: shouldWrap ? "break-word" : "normal",
        ...extra,
      };
    };

    const renderDefaultTable = () => (
      <Table size="small" sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 160, whiteSpace: "nowrap" }}>
              {groupingLabelMap[grouping]}
            </TableCell>
            {aggregation.reasons.map((reason) => (
              <TableCell
                key={reason}
                align="center"
                sx={{ whiteSpace: "nowrap" }}
              >
                {reason}
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={{ whiteSpace: "nowrap", ...TOTAL_CELL_SX }}
            >
              合計
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {aggregation.data.map((row) => (
            <TableRow key={row.name}>
              <TableCell component="th" scope="row" sx={getLabelSx(row.name)}>
                {row.name}
              </TableCell>
              {aggregation.reasons.map((reason) => (
                <TableCell key={reason} align="center">
                  {(row[reason] as number) || 0}
                </TableCell>
              ))}
              <TableCell align="center" sx={TOTAL_CELL_SX}>
                {row.total}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );

    const renderDepartmentOccupationTable = () => {
      const deptOccMap: Record<string, Record<string, ReasonRow>> = {};

      aggregation.data.forEach((row) => {
        const [dept, occ] = row.name.split("×");
        if (!deptOccMap[dept]) {
          deptOccMap[dept] = {};
        }
        if (!deptOccMap[dept][occ]) {
          deptOccMap[dept][occ] = createEmptyReasonRow();
        }
        aggregation.reasons.forEach((reason) => {
          deptOccMap[dept][occ][reason] = Number(row[reason] || 0);
        });
        deptOccMap[dept][occ].total = row.total;
      });

      const departments = Object.keys(deptOccMap).sort(sortDepartments);

      return (
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>部署</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>職種</TableCell>
              {aggregation.reasons.map((reason) => (
                <TableCell
                  key={reason}
                  align="center"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {reason}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ whiteSpace: "nowrap", ...TOTAL_CELL_SX }}
              >
                合計
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {departments.map((dept) => {
              const occupations = Object.keys(deptOccMap[dept]).sort((a, b) =>
                a.localeCompare(b),
              );
              const deptTotal = sumReasonRows(occupations.map((occ) => deptOccMap[dept][occ]));

              return (
                <React.Fragment key={dept}>
                  {occupations.map((occ, index) => (
                    <TableRow key={`${dept}-${occ}`}>
                      {index === 0 && (
                        <TableCell
                          rowSpan={occupations.length + 1}
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                            ...getLabelSx(dept, { verticalAlign: "middle", textAlign: "center" }),
                          }}
                        >
                          {dept}
                        </TableCell>
                      )}
                      <TableCell sx={getLabelSx(occ)}>{occ}</TableCell>
                      {renderReasonCells(deptOccMap[dept][occ])}
                      <TableCell align="center" sx={TOTAL_CELL_SX}>
                        {deptOccMap[dept][occ].total}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f8f8f8" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>合計</TableCell>
                    {renderReasonCells(deptTotal.totals, { isBold: true })}
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold", ...TOTAL_CELL_SX }}
                    >
                      {deptTotal.totalCount}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      );
    };

    const renderDepartmentTimingTable = () => {
      const deptTimingMap: Record<string, Record<string, ReasonRow>> = {};
      const extraTimings = new Set<string>();

      aggregation.data.forEach((row) => {
        const [dept, timing] = row.name.split("×");
        if (!deptTimingMap[dept]) {
          deptTimingMap[dept] = {};
        }
        if (!deptTimingMap[dept][timing]) {
          deptTimingMap[dept][timing] = createEmptyReasonRow();
        }
        aggregation.reasons.forEach((reason) => {
          deptTimingMap[dept][timing][reason] = Number(row[reason] || 0);
        });
        deptTimingMap[dept][timing].total = row.total;

        if (!orderedTimings.includes(timing)) {
          extraTimings.add(timing);
        }
      });

      const timingOrder = [...orderedTimings, ...Array.from(extraTimings)];
      const departments = Object.keys(deptTimingMap).sort(sortDepartments);

      // 各部署にすべてのタイミング行を用意
      departments.forEach((dept) => {
        timingOrder.forEach((timing) => {
          if (!deptTimingMap[dept][timing]) {
            deptTimingMap[dept][timing] = createEmptyReasonRow();
            deptTimingMap[dept][timing].total = 0;
          }
        });
      });

      return (
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>部署</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>観察タイミング</TableCell>
              {aggregation.reasons.map((reason) => (
                <TableCell
                  key={reason}
                  align="center"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {reason}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ whiteSpace: "nowrap", ...TOTAL_CELL_SX }}
              >
                合計
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {departments.map((dept) => {
              const deptRows = timingOrder.map((timing) => deptTimingMap[dept][timing]);
              const deptTotal = sumReasonRows(deptRows);

              return (
                <React.Fragment key={dept}>
                  {timingOrder.map((timing, index) => (
                    <TableRow key={`${dept}-${timing}`}>
                      {index === 0 && (
                        <TableCell
                          rowSpan={timingOrder.length + 1}
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                            ...getLabelSx(dept, { verticalAlign: "middle", textAlign: "center" }),
                          }}
                        >
                          {dept}
                        </TableCell>
                      )}
                      <TableCell sx={getLabelSx(timing)}>{timing}</TableCell>
                      {renderReasonCells(deptTimingMap[dept][timing])}
                      <TableCell align="center" sx={TOTAL_CELL_SX}>
                        {deptTimingMap[dept][timing].total}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f8f8f8" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>合計</TableCell>
                    {renderReasonCells(deptTotal.totals, { isBold: true })}
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold", ...TOTAL_CELL_SX }}
                    >
                      {deptTotal.totalCount}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      );
    };

    const renderDepartmentOccupationTimingTable = () => {
      const deptOccTimingMap: Record<
        string,
        Record<string, Record<string, ReasonRow>>
      > = {};
      const extraTimings = new Set<string>();

      aggregation.data.forEach((row) => {
        const [dept, occ, timing] = row.name.split("×");
        if (!deptOccTimingMap[dept]) {
          deptOccTimingMap[dept] = {};
        }
        if (!deptOccTimingMap[dept][occ]) {
          deptOccTimingMap[dept][occ] = {};
        }
        if (!deptOccTimingMap[dept][occ][timing]) {
          deptOccTimingMap[dept][occ][timing] = createEmptyReasonRow();
        }
        aggregation.reasons.forEach((reason) => {
          deptOccTimingMap[dept][occ][timing][reason] = Number(row[reason] || 0);
        });
        deptOccTimingMap[dept][occ][timing].total = row.total;

        if (!orderedTimings.includes(timing)) {
          extraTimings.add(timing);
        }
      });

      const timingOrder = [...orderedTimings, ...Array.from(extraTimings)];
      const departments = Object.keys(deptOccTimingMap).sort(sortDepartments);

      departments.forEach((dept) => {
        Object.keys(deptOccTimingMap[dept]).forEach((occ) => {
          timingOrder.forEach((timing) => {
            if (!deptOccTimingMap[dept][occ][timing]) {
              deptOccTimingMap[dept][occ][timing] = createEmptyReasonRow();
              deptOccTimingMap[dept][occ][timing].total = 0;
            }
          });
        });
      });

      return (
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>部署</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>職種</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>観察タイミング</TableCell>
              {aggregation.reasons.map((reason) => (
                <TableCell
                  key={reason}
                  align="center"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {reason}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ whiteSpace: "nowrap", ...TOTAL_CELL_SX }}
              >
                合計
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {departments.map((dept) => {
              const occupations = Object.keys(deptOccTimingMap[dept]).sort((a, b) =>
                a.localeCompare(b),
              );
              const deptTotalRows: ReasonRow[] = [];

              occupations.forEach((occ) => {
                timingOrder.forEach((timing) => {
                  deptTotalRows.push(deptOccTimingMap[dept][occ][timing]);
                });
              });

              const deptTotal = sumReasonRows(deptTotalRows);
              const deptRowSpan = occupations.length * timingOrder.length + 1;

              return (
                <React.Fragment key={dept}>
                  {occupations.map((occ, occIndex) => (
                    <React.Fragment key={`${dept}-${occ}`}>
                      {timingOrder.map((timing, timingIndex) => {
                        const isFirstOccupationRow = timingIndex === 0;
                        return (
                          <TableRow key={`${dept}-${occ}-${timing}`}>
                            {occIndex === 0 && timingIndex === 0 && (
                              <TableCell
                                rowSpan={deptRowSpan}
                                sx={{
                                  fontWeight: "bold",
                                  backgroundColor: "#f5f5f5",
                                  ...getLabelSx(dept, { verticalAlign: "middle", textAlign: "center" }),
                                }}
                              >
                                {dept}
                              </TableCell>
                            )}
                            {isFirstOccupationRow && (
                              <TableCell rowSpan={timingOrder.length} sx={getLabelSx(occ)}>
                                {occ}
                              </TableCell>
                            )}
                            <TableCell sx={getLabelSx(timing)}>{timing}</TableCell>
                            {renderReasonCells(deptOccTimingMap[dept][occ][timing])}
                            <TableCell align="center" sx={TOTAL_CELL_SX}>
                              {deptOccTimingMap[dept][occ][timing].total}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f8f8f8" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>合計</TableCell>
                    {renderReasonCells(deptTotal.totals, { isBold: true })}
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold", ...TOTAL_CELL_SX }}
                    >
                      {deptTotal.totalCount}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      );
    };

    useImperativeHandle(
      ref,
      () => ({
        setGrouping: (newGrouping: ReasonAggregationGrouping) =>
          setGrouping(newGrouping),
        getCurrentGrouping: () => grouping,
      }),
      [grouping],
    );

    const isTableOnlyGrouping = useMemo(
      () =>
        ["departmentOccupation", "departmentTiming", "departmentOccupationTiming"].includes(
          grouping,
        ),
      [grouping],
    );

    if (!hasReasonData) {
      return (
        <Box sx={{ width: "100%" }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Warning sx={{ mr: 1 }} /> 未実施理由
            </Typography>
            <Alert severity="info">未実施データがありません。</Alert>
          </Paper>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%" }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Warning sx={{ mr: 1 }} /> 未実施理由
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle2" gutterBottom>
                理由入力率
              </Typography>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h4">
                  {reasonStats.reasonInputRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({reasonStats.withReasonCount}/
                  {reasonStats.totalNonCompliance}件)
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                理由が入力された未実施件数の割合
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle2" gutterBottom>
                未入力理由件数
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h4">
                  {reasonStats.withoutReasonCount}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                未実施かつ理由未入力の件数
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle2" gutterBottom>
                未実施件数（総数）
              </Typography>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h4">
                  {reasonStats.totalNonCompliance}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  件
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                未実施の全件数
              </Typography>
            </Paper>
          </Box>

          <Box sx={{ height: "auto", mb: 3 }} className="reason-summary-chart">
            <Typography variant="subtitle1" gutterBottom>
              理由一覧
            </Typography>
            <Box sx={{ height: reasonChartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={allReasons}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={reasonLabelWidth} />
                  <Tooltip content={renderReasonSummaryTooltip} />
                  <Bar
                    dataKey="value"
                    isAnimationActive={!isChartCaptureMode}
                  >
                    <LabelList dataKey="value" position="right" />
                    {allReasons.map((entry) => (
                      <Cell
                        key={`reason-${entry.name}`}
                        fill={reasonColors[entry.name]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <ReasonLegend
                reasons={allReasons.map((reason) => reason.name)}
                reasonColors={reasonColors}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="subtitle1">
              理由別積み上げ棒グラフ・クロス集計
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="reason-grouping-label">集計軸</InputLabel>
              <Select
                labelId="reason-grouping-label"
                value={grouping}
                label="集計軸"
                onChange={(event) =>
                  setGrouping(event.target.value as ReasonAggregationGrouping)
                }
              >
                <MenuItem value="department">部署別</MenuItem>
                <MenuItem value="occupation">職種別</MenuItem>
                <MenuItem value="timing">タイミング別</MenuItem>
                <MenuItem value="departmentOccupation">部署×職種別</MenuItem>
                <MenuItem value="departmentTiming">
                  部署×観察タイミング
                </MenuItem>
                <MenuItem value="departmentOccupationTiming">
                  部署×職種×観察タイミング
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {aggregation.data.length === 0 ? (
            <Alert severity="info">未実施理由のデータがありません。</Alert>
          ) : (
            <>
              {!isTableOnlyGrouping && (
              <Box
                className="reason-stacked-chart-container"
                data-dashboard-capture-state={grouping}
                data-testid="reason-stacked-chart-container"
                sx={{ mb: 3 }}
              >
                <Box sx={{ height: 420 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        layout="vertical"
                        data={aggregation.data}
                        margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                      >
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="name" width={200} />
                        <Tooltip itemSorter={tooltipItemSorter} />
                        {aggregation.reasons.map((reason) => (
                          <Bar
                            key={reason}
                            dataKey={reason}
                            stackId="reasons"
                            fill={reasonColors[reason]}
                            name={reason}
                            isAnimationActive={!isChartCaptureMode}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <ReasonLegend
                      reasons={aggregation.reasons}
                      reasonColors={reasonColors}
                    />
                  </Box>
                </Box>
              )}

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                このテーブルは横スクロールできます。
              </Typography>
              <TableContainer sx={{ overflowX: "auto" }}>
                {grouping === "departmentOccupation"
                  ? renderDepartmentOccupationTable()
                  : grouping === "departmentTiming"
                    ? renderDepartmentTimingTable()
                    : grouping === "departmentOccupationTiming"
                      ? renderDepartmentOccupationTimingTable()
                      : renderDefaultTable()}
              </TableContainer>
            </>
          )}
        </Paper>
      </Box>
    );
  },
);

export default ReasonTab;
